import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { CountUp, IconBadge } from '@/components/atoms';
import { type CompanyFact } from '@/hooks/useCompanyStory';

/**
 * One fact about the company, drawn as a card rather than as a landmark.
 *
 * This is the treatment for a compact band, where the three facts stack in a
 * column beside the narrative instead of running across underneath it.
 * `StoryFact` sets its figure in display type, which works across a row and
 * fails down a column: three 40px numerals stacked vertically read as a
 * comparison the band is not making, and they out-shout the sentences that are
 * supposed to be the point.
 *
 * So the card is laid out across instead of down — badge, then the figure with
 * its label above and its note below. That keeps each one to about one line of
 * height per part, which is what lets three of them sit opposite two sentences
 * without the band growing a screenful. It is also the same shape at every
 * width: the row never becomes a stack, because there are only three elements
 * in it and the longest is a city name.
 *
 * Still a term-and-definition pair, and still in the order a description list
 * requires — `dt` before `dd` in the DOM, with the hierarchy carried by type
 * rather than by `order`.
 *
 * `background.paper` over the band's own surface, which is a real tile in both
 * schemes and on either tone: white on #F2EFEF or #FAF8F8 in light, #1A1819 on
 * #242122 or #101012 in dark. The divider keeps the edge legible in the dark
 * pairing, where the two surfaces are close.
 */
export function StoryFactCard({ fact }: { fact: CompanyFact }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: 4, md: 5 },
        p: { xs: 4, md: 5 },
        height: '100%',
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: 'border-color 250ms ease, transform 250ms ease',
        '&:hover': { borderColor: 'primary.main' },
        '@media (hover: hover)': { '&:hover': { transform: 'translateY(-2px)' } },
      }}
    >
      <IconBadge name={fact.icon} size={40} shape="circle" tone="tint" />

      <Stack spacing={1} sx={{ minWidth: 0 }}>
        <Typography component="dt" variant="overline" color="text.secondary">
          {fact.label}
        </Typography>
        <Typography
          component="dd"
          sx={{
            m: 0,
            fontFamily: 'h1.fontFamily',
            fontWeight: 800,
            // A step below the rail's 2/2.5rem: this figure sits beside a
            // sentence rather than anchoring a row of its own.
            fontSize: { xs: '1.5rem', md: '1.75rem' },
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
          }}
        >
          {fact.value === null ? fact.display : <CountUp value={fact.value} grouped={false} />}
        </Typography>
        <Typography component="dd" variant="body2" color="text.secondary" sx={{ m: 0 }}>
          {fact.note}
        </Typography>
      </Stack>
    </Box>
  );
}
